import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NotificationConstants } from 'angular/constants/NotificationConstants';
import { embedded } from '../../../angular/utils/config';
import { UtilService } from '../../../app/_services/util.service';

export type GrowlType = 'danger' | 'success' | 'warning' | 'info';
export type GrowlOptions = {
    delay?: number;
    allowDismiss?: boolean;
    timestamp?: boolean;
    errorCode?: string;
    statusCode?: number;
    element?: string;
    allowHtml?: boolean;
};

@Injectable()
export class Growl {
    constructor(
        private sanitizer: DomSanitizer,
        private utilService: UtilService
    ) {}

    public show(
        text: string,
        type: GrowlType,
        options: GrowlOptions = {}
    ): NotifyReturn {
        const errorCode: string = options.errorCode
            ? 'Error Code: ' + options.errorCode
            : '';
        const statusCode: string = options.statusCode
            ? 'Status Code: ' + options.statusCode
            : '';
        const delay: number =
            options.delay == null
                ? type === 'danger'
                    ? 0
                    : NotificationConstants.duration
                : options.delay;
        const sanitizedText: string = options.allowHtml
            ? this.sanitizer.sanitize(SecurityContext.HTML, text)
            : this.sanitizer.sanitize(
                  SecurityContext.HTML,
                  this.utilService.escapeHtml(text)
              );

        const icon =
            type === 'success'
                ? '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-check-big-icon lucide-circle-check-big"><path d="M21.801 10A10 10 0 1 1 17 3.335"/><path d="m9 11 3 3L22 4"/></svg>'
                : type === 'warning'
                  ? '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-alert-icon lucide-circle-alert"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>'
                  : type === 'danger'
                    ? '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-x-icon lucide-circle-x"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>'
                    : '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-x-icon lucide-circle-x"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>';

        return $.notify(<NotifyOptions>{}, {
            element: options.element || '#growl-container',
            position: null,
            type: type,
            allow_dismiss:
                options.allowDismiss != null ? options.allowDismiss : true,
            newest_on_top: false,
            showProgressbar: delay > 0,
            placement: {
                from: 'top',
                align: 'right'
            },
            animate: {
                enter: 'animated fadeInUp',
                exit: 'animated fadeOutRight'
            },
            offset: {
                x: 20,
                y: embedded ? 20 : 100
            },
            spacing: 10,
            z_index: 9999,
            delay: delay,
            timer: 1000,
            mouse_over: 'pause',
            template: `
<div data-notify="container" class="col-xs-11 col-sm-3 growl alert alert-{0}" role="alert">
    <button type="button" aria-hidden="true" class="close" data-notify="dismiss">×</button>
    ${icon}
    <div class="content">
        ${sanitizedText}<br>
        <span class="all-small-caps">${statusCode}${
            statusCode && errorCode ? ' - ' : ''
        }${errorCode}</span>
    </div>
    <div class="progress" data-notify="progressbar">
        <div class="progress-bar progress-bar-{0} width_0" role="progressbar"></div>
    </div>
</div>`
        });
    }
}

function initGrowlContainer() {
    if (!embedded) return;

    const element = document.getElementById('growl-container');
    const onFrame = () => {
        const scrollPos = window.parent.scrollY;
        element.style.top = `${scrollPos}px`;
        requestAnimationFrame(onFrame);
    };
    requestAnimationFrame(onFrame);
}
initGrowlContainer();
